import { reviver } from '@thinkalpha/common/serialization/serializer.js';
import {
    NewWorkspace,
    UpdatedWorkspace,
    WidgetContainer,
    Workspace,
    WorkspaceBundle,
} from '@thinkalpha/platform-ws-client/contracts/workspace.js';
import type { WidgetTab } from '@thinkalpha/platform-ws-client/contracts/workspace.js';
import { isSelfOwnedWidgetType } from 'src/services/WorkspaceService/selfOwnedWidgetTypes';
import { z } from 'zod';

// Widget validation belongs to the individual widget. A stale or unknown tab must
// not prevent its siblings from loading, and client-only fields must survive.
const widgetIdentity = z.object({ type: z.string() });
const widgetPayload = z.custom<z.output<typeof widgetIdentity>>((value) => widgetIdentity.safeParse(value).success);
const workspaceWidgetCodec = z.codec(widgetPayload, widgetPayload, {
    decode: (widget) => {
        if (isSelfOwnedWidgetType(widget.type)) return widget;
        // Compatibility boundary for widgets still owned by Redux. Keep their
        // historical hydration until each widget adopts its own state schema.
        return JSON.parse(JSON.stringify(widget), reviver);
    },
    // Self-owned widgets are already encoded by their state schema. Legacy
    // widgets can still carry js-joda values from their Redux contract projection.
    encode: (widget) => (isSelfOwnedWidgetType(widget.type) ? widget : JSON.parse(JSON.stringify(widget))),
});

const containers = WidgetContainer.extend({
    // WidgetTab is exported as an opaque ZodType, so its envelope cannot be
    // extended. Check this projection against the generated contract instead.
    tabs: z.array(
        z.object({
            id: z.string(),
            name: z.string().nullable().optional(),
            widget: workspaceWidgetCodec,
        }) satisfies z.ZodType<Omit<z.output<typeof WidgetTab>, 'widget'> & { widget: z.output<typeof widgetPayload> }>,
    ),
}).array();

export const workspaceCodec = Workspace.extend({ containers });
export const workspaceBundleCodec = WorkspaceBundle.extend({ workspace: workspaceCodec });
export const newWorkspaceCodec = NewWorkspace.extend({ containers });
export const updatedWorkspaceCodec = UpdatedWorkspace.extend({ containers });
export type DecodedWorkspace = z.output<typeof workspaceCodec>;
